import axios, { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { logError } from '../utils/errorReporting';

// Add type augmentation for Axios config to support metadata
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    metadata?: {
      startTime: number;
    };
  }
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    
    if (token && config.headers) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Add request start time for tracking response time
    config.metadata = { startTime: new Date().getTime() };
    
    return config;
}, error => {
    return Promise.reject(error);
});

api.interceptors.response.use(
    (response: AxiosResponse) => {
        // Calculate response time
        const config = response.config;
        if (config.metadata?.startTime) {
            const responseTime = new Date().getTime() - config.metadata.startTime;
            
            // Only log slow responses (> 1000ms)
            if (responseTime > 1000) {
                console.warn(`[API] Slow response detected (${responseTime}ms):`, {
                    url: config.url,
                    method: config.method,
                    status: response.status
                });
            }
        }
        return response;
    },
    (error: AxiosError) => {
        // Calculate response time for errors too
        if (error.config?.metadata?.startTime) {
            const responseTime = new Date().getTime() - error.config.metadata.startTime;
            
            // Add response time to error logs
            if (!error.response) {
                console.error('[API] Network error:', error.message, {
                    url: error.config?.url,
                    method: error.config?.method,
                    timeoutSet: error.config?.timeout || 'default',
                    responseTime: `${responseTime}ms`,
                    timestamp: new Date().toISOString(),
                    onLine: navigator.onLine,
                    effectiveType: (navigator as any).connection?.effectiveType || 'unknown',
                    actionPath: window.location.pathname
                });
            }
        }
        
        if (error.response?.status === 401) {
            // Capture diagnostic info before logout to help debug unexpected 401s
            const token = localStorage.getItem('token');
            let tokenInfo = 'no token';
            if (token) {
                try {
                    const decoded = jwtDecode<{ exp: number }>(token);
                    const expiresAt = new Date(decoded.exp * 1000);
                    const now = new Date();
                    tokenInfo = expiresAt < now
                        ? `expired ${Math.round((now.getTime() - expiresAt.getTime()) / 60000)} mins ago`
                        : `valid, expires in ${Math.round((expiresAt.getTime() - now.getTime()) / 60000)} mins`;
                } catch {
                    tokenInfo = 'invalid/malformed token';
                }
            }

            const diagnosticContext = {
                component: 'api-interceptor-admin',
                action: 'unexpected-401-logout',
                endpoint: error.config?.url,
                method: error.config?.method,
                responseData: error.response?.data,
                currentPath: window.location.pathname,
                tokenState: tokenInfo,
                timestamp: new Date().toISOString(),
                networkOnline: navigator.onLine,
                sessionStorageTrackId: sessionStorage.getItem('activeTrackId'),
            };

            console.error('[API] Unexpected 401 - forcing logout:', diagnosticContext);
            logError(new Error(`Unexpected 401 from ${error.config?.method} ${error.config?.url}`), diagnosticContext);

            localStorage.removeItem('token');
            window.location.href = '/login';
        }
        
        return Promise.reject(error);
    }
); 